import { useState, useRef, useEffect } from 'react';
import { EVENTS_MAP } from '../../types/events';

export const usePlayerControlsEvents = ({
  mode,
  muxPlayerRef,
}: {
  mode: 'AIRPLAY' | 'CHROMECAST' | 'CC';
  muxPlayerRef: React.RefObject<any>;
}) => {
  const events = EVENTS_MAP[mode];

  const [isOn, setIsOn] = useState(false);
  const [isSupported, setIsSupported] = useState<boolean | null>(null);

  const controllerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const player = muxPlayerRef.current;
    if (!player) return;

    const findController = () => {
      const theme = player.shadowRoot?.querySelector('media-theme') as HTMLElement | null;
      const controller = theme?.shadowRoot?.querySelector('media-controller') as HTMLElement | null;
      if (controller) controllerRef.current = controller;
    };

    findController();

    if (!controllerRef.current) {
      const mo = new MutationObserver(() => {
        if (!controllerRef.current) findController();
        if (controllerRef.current) mo.disconnect();
      });
      mo.observe(player, { childList: true, subtree: true });
      return () => mo.disconnect();
    }
  }, [muxPlayerRef]);

  useEffect(() => {
    const ctrl = controllerRef.current;
    if (!ctrl) return;

    const checkAvailability = () =>
      events.unavailableAttribute
        ? setIsSupported(
            !(
              ctrl.getAttribute(events.unavailableAttribute) === 'unsupported' ||
              ctrl.getAttribute(events.unavailableAttribute) === 'unavailable'
            ),
          )
        : setIsSupported(true);
    checkAvailability();

    const update = () => setIsOn(ctrl.hasAttribute(events.stateAttributeName));

    update();

    const obs = new MutationObserver(update);
    obs.observe(ctrl, { attributes: true, attributeFilter: [events.stateAttributeName] });

    const availabilityObserver = new MutationObserver(checkAvailability);
    if (events.unavailableAttribute)
      availabilityObserver.observe(ctrl, {
        attributes: true,
        attributeFilter: [events.unavailableAttribute],
      });
    return () => {
      obs.disconnect();
      if (events.unavailableAttribute) availabilityObserver.disconnect();
    };
  }, [controllerRef.current]);

  const handleToggle = () => {
    const ctrl = controllerRef.current;
    if (!ctrl) return;

    ctrl.dispatchEvent(
      new CustomEvent(isOn ? events.stopEventName : events.startEventName, {
        bubbles: true,
        composed: true,
      }),
    );
  };

  return { isSupported, handleToggle, isOn };
};
